// Kleiner globaler Zustand ohne Bibliothek: Postfächer, Zähler, aktuelle Ansicht, Liste, Live-Updates.
import { useRef, useSyncExternalStore } from 'react';
import type {
  Counts,
  Mailbox,
  Me,
  ServerEvent,
  Signature,
  ThreadListItem,
  View,
} from '@mail/shared';
import { api, Unauthorized } from './api.ts';

export type State = {
  me: (Me & { via: string }) | null;
  auth: 'laden' | 'login' | 'ok';
  mailboxes: Mailbox[];
  signatures: Signature[];
  counts: Counts | null;
  view: View;
  mailbox: number | null; // Filter auf ein Postfach
  q: string; // Suchfeld in der Liste
  sort: 'neu' | 'prio';
  threads: ThreadListItem[];
  loading: boolean;
  listError: boolean; // letztes Laden der Liste fehlgeschlagen (API kurz weg) – wird automatisch wiederholt
  listKey: string; // Ansicht|Postfach|Suche, zu der `threads` gehört – die Liste zeigt nie Einträge einer anderen
  listEnde: boolean; // alles geladen (sonst „Ältere laden“, Archiv)
  selected: number | null;
  sideMini: boolean;
  sideOpen: boolean; // schmal: Seitenleiste eingeblendet
  crmOpen: boolean;
  live: 'verbunden' | 'getrennt';
  lastSync: string | null;
  updateReady: boolean; // neue Version der App liegt bereit (Service Worker wartet) – Knopf „Neu laden“
};

const stored = <T>(k: string, d: T): T => {
  try {
    const v = localStorage.getItem(k);
    return v == null ? d : (JSON.parse(v) as T);
  } catch {
    return d;
  }
};
const remember = (k: string, v: unknown) => {
  try {
    localStorage.setItem(k, JSON.stringify(v));
  } catch {
    /* privates Fenster o. ä. */
  }
};

let state: State = {
  me: null,
  auth: 'laden',
  mailboxes: [],
  signatures: [],
  counts: null,
  view: 'inbox',
  mailbox: null,
  q: '',
  sort: stored('mm.sort', 'neu'),
  threads: [],
  loading: false,
  listError: false,
  listKey: '',
  listEnde: true,
  selected: null,
  sideMini: stored('mm.sideMini', false),
  sideOpen: false,
  crmOpen: stored('mm.crmOpen', typeof window !== 'undefined' ? window.innerWidth > 1180 : true),
  live: 'getrennt',
  lastSync: null,
  updateReady: false,
};
const listeners = new Set<() => void>();
function set(patch: Partial<State> | ((s: State) => Partial<State>)) {
  state = { ...state, ...(typeof patch === 'function' ? patch(state) : patch) };
  listeners.forEach((l) => l());
}
const shallowEqual = (a: unknown, b: unknown) => {
  if (Object.is(a, b)) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || !a || !b) return false;
  const ka = Object.keys(a),
    kb = Object.keys(b);
  return (
    ka.length === kb.length &&
    ka.every((k) => Object.is((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]))
  );
};
// Selector darf ein neues Objekt liefern — gleiche Werte ergeben dieselbe Referenz (sonst Endlosschleife)
export function useStore<T>(sel: (s: State) => T): T {
  const last = useRef<{ st: State; v: T } | null>(null);
  const snap = () => {
    const l = last.current;
    if (l && l.st === state) return l.v;
    const v = sel(state);
    const out = l && shallowEqual(l.v, v) ? l.v : v;
    last.current = { st: state, v: out };
    return out;
  };
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => {
        listeners.delete(l);
      };
    },
    snap,
    snap,
  );
}
export const getState = () => state;

// ---------- Laden ----------
let listSeq = 0;
const SEITE = 150; // Server-Standard (limit)
export const listKeyFor = (view: View, mailbox: number | null, q: string) =>
  `${view}|${mailbox ?? ''}|${q.trim()}`;
export async function loadList() {
  const { view, mailbox, q, sort } = state;
  const seq = ++listSeq;
  set({ loading: true });
  try {
    const r = await api.threads({
      mailbox,
      view,
      q,
      ...(sort === 'prio' && view === 'inbox' ? { sort: 'prio' } : {}),
    });
    if (seq !== listSeq) return;
    set({
      threads: r.threads,
      loading: false,
      listError: false,
      listKey: listKeyFor(view, mailbox, q),
      listEnde: !!q || r.threads.length < SEITE,
    });
  } catch (e) {
    if (seq !== listSeq) return;
    if (e instanceof Unauthorized) {
      set({ auth: 'login', loading: false });
      return;
    }
    // API gerade nicht erreichbar (Deploy, Netz): nicht still die alte Liste stehen lassen – Hinweis zeigen
    // und in 3 s erneut laden, solange niemand inzwischen etwas anderes angefordert hat.
    set({ loading: false, listError: true });
    setTimeout(() => {
      if (seq === listSeq) void loadList();
    }, 3000);
  }
}
// Ältere Threads nachladen (Archiv): alles vor dem letzten Eintrag der Liste
export async function loadMore() {
  const { view, mailbox, q, threads, loading, listEnde } = state;
  if (loading || listEnde || q || !threads.length) return;
  const seq = ++listSeq;
  const before = threads[threads.length - 1]!.lastDate;
  if (!before) return;
  set({ loading: true });
  try {
    const r = await api.threads({ mailbox, view, before });
    if (seq !== listSeq) return;
    const bekannt = new Set(state.threads.map((t) => t.id));
    set({
      threads: [...state.threads, ...r.threads.filter((t) => !bekannt.has(t.id))],
      loading: false,
      listEnde: r.threads.length < SEITE,
    });
  } catch (e) {
    if (seq !== listSeq) return;
    set({ loading: false });
    if (e instanceof Unauthorized) set({ auth: 'login' });
  }
}
export async function loadCounts() {
  try {
    const [counts, mailboxes] = await Promise.all([api.counts(), api.mailboxes()]);
    const synced =
      mailboxes
        .map((m) => m.lastSyncAt)
        .filter((x): x is string => !!x)
        .sort()
        .pop() ?? null;
    set({ counts, mailboxes, lastSync: synced });
  } catch (e) {
    if (e instanceof Unauthorized) set({ auth: 'login' });
  }
}
// Nach „Postfach hinzufügen“/ändern/entfernen: Postfächer und Signaturen neu laden
export async function reloadMailboxes() {
  try {
    const [mailboxes, signatures, counts] = await Promise.all([
      api.mailboxes(),
      api.signatures(),
      api.counts(),
    ]);
    set({ mailboxes, signatures, counts });
  } catch (e) {
    if (e instanceof Unauthorized) set({ auth: 'login' });
  }
}
export async function boot() {
  try {
    const me = await api.me();
    const [mailboxes, signatures] = await Promise.all([api.mailboxes(), api.signatures()]);
    set({ me, mailboxes, signatures, auth: 'ok' });
    await Promise.all([loadCounts(), loadList()]);
    connectLive();
  } catch (e) {
    set({ auth: e instanceof Unauthorized ? 'login' : 'ok' });
  }
}

// ---------- Navigation ----------
export function setView(view: View, mailbox: number | null = null) {
  set({ view, mailbox, q: '', sideOpen: false });
  void loadList();
}
export function setSort(sort: 'neu' | 'prio') {
  remember('mm.sort', sort);
  set({ sort });
  void loadList();
}
// Suchfeld in der Liste: Tippen mit kurzer Verzögerung abfragen (jede Taste eine Volltextsuche über alle
// Postfächer wäre bei 100.000 Mails zu viel), Leeren sofort.
let queryTimer: ReturnType<typeof setTimeout> | null = null;
export function setQuery(q: string) {
  set({ q, loading: true });
  if (queryTimer) clearTimeout(queryTimer);
  if (!q.trim()) {
    void loadList();
    return;
  }
  queryTimer = setTimeout(() => void loadList(), 300);
}
export function select(id: number | null) {
  set({ selected: id });
}
export function toggleSideMini() {
  set((s) => {
    remember('mm.sideMini', !s.sideMini);
    return { sideMini: !s.sideMini };
  });
}
export function toggleCrm() {
  set((s) => {
    remember('mm.crmOpen', !s.crmOpen);
    return { crmOpen: !s.crmOpen };
  });
}
export function setSideOpen(v: boolean) {
  set({ sideOpen: v });
}

// Thread lokal anpassen (optimistisch), ohne auf den Server zu warten
export function patchThread(id: number, patch: Partial<ThreadListItem>) {
  set((s) => ({ threads: s.threads.map((t) => (t.id === id ? { ...t, ...patch } : t)) }));
}
export function removeThread(id: number) {
  set((s) => ({ threads: s.threads.filter((t) => t.id !== id) }));
}
// Nächster offener Thread nach einer Aktion („Senden & erledigen“ öffnet die nächste Mail)
export function nextAfter(id: number): number | null {
  const l = state.threads;
  const i = l.findIndex((t) => t.id === id);
  const rest = l.filter((t) => t.id !== id);
  return rest[Math.min(Math.max(0, i), rest.length - 1)]?.id ?? null;
}

// ---------- Live ----------
let es: EventSource | null = null;
let reloadTimer: ReturnType<typeof setTimeout> | null = null;
const threadListeners = new Set<(ids: number[]) => void>();
const eventListeners = new Set<(ev: ServerEvent) => void>();
export function onEvent(fn: (ev: ServerEvent) => void): () => void {
  eventListeners.add(fn);
  return () => {
    eventListeners.delete(fn);
  };
}
export function onThreadsChanged(fn: (ids: number[]) => void): () => void {
  threadListeners.add(fn);
  return () => {
    threadListeners.delete(fn);
  };
}
// Der Browser verbindet eine EventSource nur nach Netzfehlern selbst neu. Antwortet der Server mit einem
// Fehlerstatus (502 während eines Deploys, 401 nach Ablauf der Sitzung), gibt er endgültig auf
// (readyState CLOSED) – dann müssen wir selbst neu verbinden, mit wachsendem Abstand (3 s … 30 s).
// Solange die Verbindung fehlt, werden Liste und Zähler jede Minute abgefragt, damit neue Mails trotzdem kommen.
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectWait = 3000;
let pollTimer: ReturnType<typeof setInterval> | null = null;
function connectLive() {
  if (es || state.auth !== 'ok') return;
  const s = new EventSource('/api/sse');
  es = s;
  s.onopen = () => {
    // Nach einer Unterbrechung (Deploy, Schlaf) Liste und Zähler auffrischen – Ereignisse können gefehlt haben
    const war = state.live;
    reconnectWait = 3000;
    stopPoll();
    set({ live: 'verbunden' });
    if (war === 'getrennt') {
      void loadList();
      void loadCounts();
    }
  };
  s.onerror = () => {
    set({ live: 'getrennt' });
    startPoll();
    if (s.readyState === EventSource.CLOSED) {
      s.close();
      if (es === s) es = null;
      scheduleReconnect();
    }
  };
  s.onmessage = (e) => {
    const ev = JSON.parse(e.data as string) as ServerEvent;
    if (ev.kind === 'hello') return;
    eventListeners.forEach((fn) => fn(ev));
    if (ev.kind === 'presence') return; // nur für die offene Konversation, keine Liste neu laden
    if (ev.kind === 'threads.changed') threadListeners.forEach((fn) => fn(ev.threadIds));
    // Liste und Zähler gebündelt neu laden (viele Ereignisse beim Sync)
    if (reloadTimer) clearTimeout(reloadTimer);
    reloadTimer = setTimeout(() => {
      reloadTimer = null;
      void loadList();
      void loadCounts();
    }, 400);
  };
}
function scheduleReconnect() {
  if (reconnectTimer) return;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    connectLive();
  }, reconnectWait);
  reconnectWait = Math.min(reconnectWait * 2, 30_000);
}
function startPoll() {
  if (pollTimer) return;
  pollTimer = setInterval(() => {
    if (state.live !== 'getrennt' || state.auth !== 'ok') return;
    void loadList();
    void loadCounts();
  }, 60_000);
}
function stopPoll() {
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = null;
}
// Fenster wieder sichtbar oder Netz wieder da: sofort neu verbinden statt auf den Timer zu warten
export function reconnectNow() {
  if (state.auth !== 'ok') return;
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  reconnectWait = 3000;
  if (es && es.readyState === EventSource.CLOSED) {
    es.close();
    es = null;
  }
  if (!es) connectLive();
  else if (state.live === 'getrennt') {
    void loadList();
    void loadCounts();
  }
}
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && state.live === 'getrennt') reconnectNow();
  });
  window.addEventListener('online', () => reconnectNow());
}

// ---------- App-Update (Service Worker) ----------
let applyUpdateFn: (() => void) | null = null;
export function setUpdateReady(apply: () => void) {
  applyUpdateFn = apply;
  set({ updateReady: true });
}
export function applyUpdate() {
  applyUpdateFn?.();
}
